import React from 'react';
import { motion } from 'framer-motion';
import { HiArrowLeft, HiInformationCircle, HiShieldCheck, HiExclamation, HiChevronRight, HiEye } from 'react-icons/hi';
import { Link } from 'react-router-dom';

// Import images
import anatomyImg from '../assets/education/anatomy.png';
import colobomaImg from '../assets/education/coloboma.png';
import ptosisImg from '../assets/education/ptosis.png';
import hordeolumExtImg from '../assets/education/hordeolum_ext.png';
import hordeolumIntImg from '../assets/education/hordeolum_int.png';
import conjunctivitisImg from '../assets/education/conj_viral.png';
import xanthelasmaImg from '../assets/education/xanthelasma.png';
import keratoconusImg from '../assets/education/keratoconus.png';
import blepharitisImg from '../assets/education/blepharitis_1.png';
import dryEyeImg from '../assets/education/dry_eye.png';
import cataractImg from '../assets/education/cataract.png';
import diabeticRetinopathyImg from '../assets/education/diabetic_retinopathy.png';
import glaucomaImg from '../assets/education/glaucoma.png';

const EyeEducation = () => {
    const anatomyParts = [
        { name: 'Cornea', desc: 'The clear front surface. Helps focus light and protects from dust/germs. Known as the "window" of the eye.' },
        { name: 'Iris', desc: 'The colored part of the eye. Functions like a camera aperture to control light entry.' },
        { name: 'Pupil', desc: 'The center opening. Dynamically adjusts its size based on environmental light levels.' },
        { name: 'Lens', desc: 'Sits behind the pupil. Fine-tunes the focus of light onto the retina.' },
        { name: 'Retina', desc: 'Light-sensitive back layer. Converts optical data into neural signals.' },
        { name: 'Optic Nerve', desc: 'The biological transmission cable. Carries visual data to the brain for interpretation.' },
        { name: 'Sclera', desc: 'The tough white outer layer. Provides structural integrity and protection.' },
        { name: 'Vitreous Humor', desc: 'The internal gel-like substance. Maintain the spherical pressure and shape.' },
    ];

    const conditions = [
        {
            id: 'coloboma',
            title: 'Coloboma',
            tag: 'Congenital',
            image: colobomaImg,
            desc: 'A congenital defect present at birth where a portion of the eyelid is absent or not fully formed, resulting in a visible gap or notch.',
            sections: [
                { title: 'Clinical Signs', items: ['Visible notch in eyelid margin', 'Incomplete eyelid closure', 'Irregular eyelid contour'] },
                { title: 'Symptoms', items: ['Excessive tearing', 'Chronic dryness', 'Redness/Irritation', 'Light sensitivity'] },
                { title: 'Care & Treatment', items: ['Early pediatric evaluation', 'Ocular lubrication', 'Surgical eyelid reconstruction'] }
            ],
            complications: ['Exposure keratitis', 'Corneal ulcers', 'Risk of infection']
        },
        {
            id: 'ptosis',
            title: 'Ptosis',
            tag: 'Eyelid Laxity',
            image: ptosisImg,
            desc: 'Drooping of the upper eyelid to a lower-than-normal position. It can affect one (unilateral) or both eyes (bilateral).',
            sections: [
                { title: 'Signs', items: ['Drooping upper lid', 'Reduced lid movement', 'Compensatory head tilting', 'Frequent eyebrow raising'] },
                { title: 'Causes', items: ['Congenital (muscle development)', 'Acquired (aging, trauma, nerve issues)'] },
                { title: 'Management', items: ['Vision monitoring in children', 'Levator muscle surgery', 'Specialist consultation'] }
            ],
            emergency: 'Seek immediate medical attention if sudden onset, double vision, or headache occurs.'
        },
        {
            id: 'hordeolum-ext',
            title: 'External Stye',
            tag: 'Acute Infection',
            image: hordeolumExtImg,
            desc: 'An acute bacterial infection of the glands at the base of the eyelashes (Zeis or Moll glands).',
            sections: [
                { title: 'Symptoms', items: ['Painful tender swelling', 'Localized yellow pus point', 'Foreign body sensation', 'Watery discharge'] },
                { title: 'Risk Factors', items: ['Poor hygiene', 'Contaminated cosmetics', 'Weak immune system'] },
                { title: 'Home Care', items: ['Warm compress (3-4x daily)', 'Lid cleaning with warm water', 'Avoid squeezing the lesion'] }
            ],
            medical: 'Usually resolves in 1-2 weeks. Consult a doctor if condition worsens.'
        },
        {
            id: 'hordeolum-int',
            title: 'Internal Stye',
            tag: 'Deep Infection',
            image: hordeolumIntImg,
            desc: 'Infection of the Meibomian glands located within the eyelid. Deeper and typically more painful than a stye.',
            sections: [
                { title: 'Diagnosis', items: ['Internal lid swelling', 'Pressure sensation', 'Blinking discomfort', 'Diffuse lid redness'] },
                { title: 'Risk Factors', items: ['Chronic blepharitis', 'Oily skin conditions', 'Frequent eye rubbing'] },
                { title: 'Treatment', items: ['Warm compress therapy', 'Antibiotic ointments', 'Possible surgical drainage'] }
            ],
            medical: 'May require oral antibiotics if the infection spreads.'
        },
        {
            id: 'blepharitis',
            title: 'Blepharitis',
            tag: 'Chronic Inflammation',
            image: blepharitisImg,
            desc: 'Chronic inflammation of the eyelid margins. Often recurrent and associated with other skin conditions.',
            sections: [
                { title: 'Symptoms', items: ['Red, swollen margins', 'Base crusting/flakes', 'Itching or burning', 'Morning sticky eyelids'] },
                { title: 'Core Types', items: ['Anterior: Affects eyelash base', 'Posterior: Oil gland dysfunction'] },
                { title: 'Hygiene Protocol', items: ['Daily eyelid margin cleaning', 'Warm compress/massage', 'Artificial tears for dryness'] }
            ],
            insight: 'Managed through consistent hygiene; symptoms recur if maintenance stops.'
        },
        {
            id: 'xanthelasma',
            title: 'Xanthelasma',
            tag: 'Liped Condition',
            image: xanthelasmaImg,
            desc: 'Yellowish, flat plaques near the inner corners of eyelids. These are deposits of cholesterol-rich material.',
            sections: [
                { title: 'Signs', items: ['Soft, yellowish plaques', 'Symmetrical appearance', 'Typically painless', 'Gradual size increase'] },
                { title: 'Risk Indicators', items: ['Hyperlipidemia', 'Diabetes Mellitus', 'Thyroid disorders'] },
                { title: 'Management', items: ['Lipid profile monitoring', 'Heart-healthy diet', 'Laser or surgical removal'] }
            ],
            note: 'Removal is for cosmetic reasons; recurrence is common.'
        },
        {
            id: 'conjunctivitis',
            title: 'Pink Eye',
            tag: 'Infectious / Allergic',
            image: conjunctivitisImg,
            desc: 'Inflammation of the thin membrane covering the white part of the eye and the inner eyelid surface.',
            sections: [
                { title: 'Symptoms', items: ['Pink or red appearance', 'Watery or purulent eyes', 'Gritty sensation', 'Morning lid crusting'] },
                { title: 'Types', items: ['Viral: Watery discharge', 'Bacterial: Thick pus discharge', 'Allergic: Watery & Itchy'] },
                { title: 'Prevention', items: ['Frequent hand washing', 'No sharing of towels/makeup', 'Daily cold compress'] }
            ],
            contagious: 'Viral and bacterial forms are highly contagious through contact.'
        },
        {
            id: 'keratoconus',
            title: 'Keratoconus',
            tag: 'Corneal Shape',
            image: keratoconusImg,
            desc: 'Progressive condition where the cornea thins and bulges outward into a cone-like shape, distorting vision.',
            sections: [
                { title: 'Visual Signs', items: ['Blurred/Distorted vision', 'Ghosting or multiple images', 'Difficulty seeing at night', 'Rx changes'] },
                { title: 'Causes', items: ['Genetic predisposition', 'Frequent eye rubbing', 'Allergic eye conditions'] },
                { title: 'Treatment', items: ['Specialized rigid lenses', 'Corneal cross-linking', 'Corneal transplant (Advanced)'] }
            ],
            advice: 'Avoid rubbing the eyes as it can worsen the corneal thinning.'
        },
    ];

    const majorConditions = [
        {
            id: 'dry-eye',
            title: 'Dry Eye Disease',
            tag: 'Lubrication Deficiency',
            image: dryEyeImg,
            desc: 'Occurs when eyes do not produce enough tears or tears evaporate too quickly, leading to chronic inflammation and surface damage.',
            sections: [
                { title: 'Symptom Profile', items: ['Burning or stinging sensation', 'Gritty or sandy feeling', 'Temporary blurred vision', 'Excessive tearing (reflex)'] },
                { title: 'Risk Factors', items: ['Prolonged screen use', 'Ageing / Menopause', 'Dry or windy environments', 'Diabetes / Thyroid disorders'] },
                { title: 'Clinical Management', items: ['20-20-20 rule for screens', 'Artificial tear drops', 'Warm compress / Hydration', 'Use of room humidifiers'] }
            ],
            insight: 'Blink frequently during screen use and avoid smoky or windy environments to minimize tear film evaporation.'
        },
        {
            id: 'cataract',
            title: 'Cataract',
            tag: 'Lens Opacification',
            image: cataractImg,
            desc: 'A condition in which the natural lens of the eye becomes cloudy, obstructing the path of light and leading to reduced clarity.',
            sections: [
                { title: 'Visual Markers', items: ['Blurred or cloudy vision', 'Significant night difficulty', 'Faded or yellowed colors', 'Increased light sensitivity'] },
                { title: 'Primary Causes', items: ['Ageing / UV Exposure', 'Diabetes Mellitus', 'Ocular Injury', 'Long-term Steroids'] },
                { title: 'Therapy Pathway', items: ['Regular eye examinations', 'Improved lighting for tasks', 'Surgical IOL replacement'] }
            ],
            medical: 'Definitive treatment involves surgical removal of the cloudy lens and replacement with a premium artificial intraocular lens.'
        },
        {
            id: 'diabetic-retinopathy',
            title: 'Diabetic Retinopathy',
            tag: 'Retinal Microvascular',
            image: diabeticRetinopathyImg,
            desc: 'A metabolic complication of diabetes that damages the retinal blood vessels, potentially leading to permanent blindness.',
            sections: [
                { title: 'Diagnostic Signs', items: ['Blurred or fluctuating vision', 'Floaters / Dark spots', 'Difficulty seeing colors', 'Sudden visual loss'] },
                { title: 'Root Causes', items: ['Long-standing diabetes', 'Poor blood sugar control', 'Systemic Hypertension', 'High cholesterol levels'] },
                { title: 'Prevention Protocol', items: ['Strict glycemic control', 'Annual retinal screening', 'Blood pressure management'] }
            ],
            medical: 'Treatment includes laser photocoagulation to seal vessels, anti-VEGF injections, or advanced vitrectomy surgery.'
        },
        {
            id: 'glaucoma',
            title: 'Glaucoma',
            tag: 'Optic Nerve Degeneration',
            image: glaucomaImg,
            desc: 'A group of progressive conditions that damage the optic nerve, often driven by excessive internal eye pressure.',
            sections: [
                { title: 'Symptom Spectrum', items: ['Gradual peripheral loss', 'Tunnel vision (late stage)', 'Acute eye pain / Headaches', 'Halos around light sources'] },
                { title: 'Critical Risk Factors', items: ['Intraocular pressure spikes', 'Family History / Genetics', 'Advanced Age', 'Corticosteroid use'] },
                { title: 'Global Care Path', items: ['Pressure screening (IOP)', 'Early nerve monitoring', 'IOP-reducing medications'] }
            ],
            medical: 'Management focuses on pressure reduction via localized eye drops, selective laser trabeculoplasty, or filtration surgery.'
        },
    ];

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
    };

    return (
        <div className="min-h-screen bg-black text-white pt-24 pb-20 selection:bg-[var(--gold-primary)]/30">
            {/* Header Section */}
            <header className="py-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(212,175,55,0.15),transparent_70%)]" />
                <div className="section-container relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-[var(--gold-primary)] transition-all font-black text-[10px] uppercase tracking-[0.4em] mb-12 group">
                            <HiArrowLeft className="group-hover:-translate-x-2 transition-transform" /> Back to Dashboard
                        </Link>
                        <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-6">
                            Knowledge <span className="text-gradient">Hub.</span>
                        </h1>
                        <p className="max-w-2xl mx-auto text-lg text-zinc-400 font-medium leading-relaxed">
                            A refined clinical library for ocular health. Explore the anatomy of sight and master the understanding of common clinical conditions.
                        </p>
                    </motion.div>
                </div>
            </header>

            {/* Anatomy Section */}
            <section className="py-24 border-y border-white/5 bg-[#050505]">
                <div className="section-container">
                    <div className="flex items-center gap-4 mb-20 justify-center">
                        <div className="w-px h-12 bg-gradient-to-b from-transparent via-[var(--gold-primary)] to-transparent" />
                        <h2 className="text-3xl font-black tracking-tight flex items-center gap-3">
                            <HiInformationCircle className="text-[var(--gold-primary)]" /> Ocular Architecture
                        </h2>
                        <div className="w-px h-12 bg-gradient-to-b from-transparent via-[var(--gold-primary)] to-transparent" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="p-10 bg-black border border-white/10 rounded-[3rem] shadow-2xl relative group"
                        >
                            <img src={anatomyImg} alt="Eye Anatomy" className="w-full h-auto rounded-[2rem] transition-transform duration-700 group-hover:scale-[1.02]" />
                            <div className="absolute inset-x-10 -bottom-6 p-6 bg-[var(--gold-primary)] text-black rounded-3xl shadow-2xl font-black text-sm italic text-center">
                                "Capture light. Send signals. Create vision."
                            </div>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {anatomyParts.map((part, idx) => (
                                <motion.div
                                    key={idx}
                                    variants={itemVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    className="p-6 bg-white/5 border border-white/5 rounded-2xl hover:border-[var(--gold-primary)]/20 transition-all hover:bg-white/[0.07]"
                                >
                                    <h4 className="text-[var(--gold-primary)] font-black text-xs uppercase tracking-widest mb-2">{part.name}</h4>
                                    <p className="text-sm text-zinc-400 leading-relaxed font-medium">{part.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Eyelid Conditions - Grid Layout */}
            <section className="py-24">
                <div className="section-container">
                    <div className="flex flex-col items-center mb-24 text-center">
                        <span className="text-[var(--gold-primary)] font-black uppercase tracking-[0.5em] text-[10px] mb-4">Diagnostic Modules</span>
                        <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">Eyelid & Corneal Cases</h2>
                        <div className="mt-8 w-24 h-1 bg-[var(--gold-primary)] rounded-full" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {conditions.map((condition) => (
                            <motion.div
                                key={condition.id}
                                variants={itemVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className="group bg-[#080808] border border-white/5 rounded-[2.5rem] overflow-hidden hover:border-[var(--gold-primary)]/30 transition-all duration-500 flex flex-col"
                            >
                                <div className="h-64 relative overflow-hidden">
                                    <img src={condition.image} alt={condition.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/20 to-transparent" />
                                    <div className="absolute bottom-8 left-8">
                                        <span className="px-3 py-1 bg-white/10 backdrop-blur-md text-white font-black text-[9px] uppercase tracking-widest rounded-md border border-white/10 mb-4 inline-block">
                                            {condition.tag}
                                        </span>
                                        <h3 className="text-4xl font-black text-white tracking-tight">{condition.title}</h3>
                                    </div>
                                </div>
                                <div className="p-8 space-y-8 flex-grow">
                                    <p className="text-lg text-zinc-300 font-medium italic leading-relaxed">{condition.desc}</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {condition.sections.map((s, sIdx) => (
                                            <div key={sIdx} className="space-y-4">
                                                <h4 className="text-[10px] font-black uppercase tracking-widest text-[var(--gold-primary)] flex items-center gap-2">
                                                    <HiChevronRight /> {s.title}
                                                </h4>
                                                <ul className="space-y-2">
                                                    {s.items.map((item, iIdx) => (
                                                        <li key={iIdx} className="text-xs text-zinc-500 font-bold flex items-start gap-2">
                                                            <div className="w-1 h-1 bg-[var(--gold-primary)]/40 rounded-full mt-1.5" />
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MAJOR CONDITIONS - FULL WIDTH IMMERSIVE LAYOUT */}
            <section className="pt-24 space-y-32">
                <div className="section-container text-center mb-16">
                    <span className="text-[var(--gold-primary)] font-black uppercase tracking-[0.5em] text-[10px] mb-4">Critical Analysis</span>
                    <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight">Major Ocular Pathologies</h2>
                </div>

                {majorConditions.map((condition, idx) => (
                    <div key={condition.id} className="relative group">
                        <section className="min-h-screen flex flex-col justify-center items-center relative overflow-hidden bg-zinc-950">
                            {/* Full Width Impact Image */}
                            <div className="absolute inset-0 z-0">
                                <motion.img 
                                    initial={{ scale: 1.1, opacity: 0 }}
                                    whileInView={{ scale: 1, opacity: 0.3 }}
                                    transition={{ duration: 1.5 }}
                                    src={condition.image} 
                                    alt={condition.title} 
                                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000" 
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent z-10" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black z-10" />
                            </div>

                            <div className="section-container relative z-20 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                                <motion.div 
                                    initial={{ opacity: 0, x: -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 1 }}
                                    className="lg:col-span-7 space-y-10"
                                >
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-4">
                                            <span className="w-12 h-px bg-[var(--gold-primary)]" />
                                            <span className="text-[var(--gold-primary)] font-black uppercase tracking-[0.4em] text-xs">{condition.tag}</span>
                                        </div>
                                        <h3 className="text-5xl md:text-8xl font-black tracking-tighter text-white leading-none">
                                            {condition.title.split(' ').map((word, i) => (
                                                <span key={i} className={i === 1 ? 'text-gradient block' : 'block'}>{word} </span>
                                            ))}
                                        </h3>
                                        <p className="text-xl md:text-2xl text-zinc-300 font-medium italic leading-relaxed max-w-2xl border-l-4 border-[var(--gold-primary)] pl-8">
                                            {condition.desc}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                                        {condition.sections.map((s, sIdx) => (
                                            <div key={sIdx} className="space-y-4">
                                                <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-[var(--gold-primary)] border-b border-[var(--gold-primary)]/20 pb-2">{s.title}</h4>
                                                <ul className="space-y-3">
                                                    {s.items.map((item, iIdx) => (
                                                        <li key={iIdx} className="text-xs text-zinc-400 font-bold leading-relaxed flex items-start gap-3">
                                                            <HiEye className="text-[var(--gold-primary)] mt-0.5 flex-shrink-0" />
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>

                                    {(condition.medical || condition.insight) && (
                                        <div className="p-8 bg-[var(--gold-primary)]/5 border border-[var(--gold-primary)]/20 rounded-[2rem] flex items-center gap-6">
                                            <HiShieldCheck className="text-[var(--gold-primary)] flex-shrink-0" size={32} />
                                            <p className="text-sm text-[var(--gold-primary)] font-black italic">
                                                {condition.medical || condition.insight}
                                            </p>
                                        </div>
                                    )}
                                </motion.div>

                                <motion.div 
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 1 }}
                                    className="lg:col-span-5 relative hidden lg:block"
                                >
                                    <div className="aspect-square relative group">
                                        <div className="absolute inset-0 bg-[var(--gold-primary)]/20 blur-[100px] rounded-full group-hover:bg-[var(--gold-primary)]/30 transition-colors" />
                                        <img 
                                            src={condition.image} 
                                            alt="Clinical Close-up" 
                                            className="relative z-10 w-full h-full object-cover rounded-[3rem] border border-white/10 shadow-2xl skew-y-3 group-hover:skew-y-0 transition-transform duration-700" 
                                        />
                                        <div className="absolute -bottom-6 -right-6 w-32 h-32 border-r-4 border-b-4 border-[var(--gold-primary)]/30 rounded-br-[3rem]" />
                                    </div>
                                </motion.div>
                            </div>
                        </section>
                    </div>
                ))}
            </section>

            {/* Footer Disclaimer */}
            <footer className="py-24 border-t border-white/5 bg-zinc-950">
                <div className="section-container text-center">
                    <div className="max-w-3xl mx-auto space-y-8">
                        <div className="w-12 h-1 bg-white/10 mx-auto rounded-full" />
                        <h4 className="text-[var(--gold-primary)] font-black text-xs uppercase tracking-[0.5em]">Medical Responsibility</h4>
                        <p className="text-sm text-zinc-500 font-medium leading-relaxed">
                            This Information Hub is provided for educational awareness only. It does not constituent medical diagnosis or professional health advice. In Case of emergency or deteriorating visual symptoms, please contact your eye care provider immediately.
                        </p>
                        <div className="pt-10 flex items-center justify-center gap-6 text-zinc-600 font-black text-[10px] uppercase tracking-widest">
                            <span>VisionCarePlus Academy</span>
                            <div className="w-1 h-1 bg-zinc-800 rounded-full" />
                            <span>v1.5.0</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default EyeEducation;
